import React, {useCallback} from 'react';
import './TransactionCard.less';
import '../../stylesheets/form.less';
import Transaction from "../../entities/transaction";
import util from "../../lib/util";
import OkCancel from "../../ui/Buttons/OkCancel";
import FormElement from "../../lib/FormElement";
import CaseHelper from "../../Case/CaseHelper";
import IconButton from "../../ui/IconButton/IconButton";
import Contact from "../../entities/contact";
import FinancialHelper from "../FinancialHelper";
import * as DateUtil from "../../lib/dateutils";
import {firmCalendarDay} from "../../lib/firmTime";
import failureLadder from '../../lib/failureLadder';
import i18n from '../../i18n/i18n';
import HelpMessage from '../../i18n/HelpMessage';
import {formatFMLFromId, formatLFM, sortContacts} from "../../Contacts/ContactFormat";
import {isPortalUser} from "../../lib/picos/currentLogin";
import Icons from "../../ui/IconButton/Icons";
import type {RootState} from "../../@types/store";
import {uuid} from "../../@types/entity";
import {TransactionChildCardProps} from "./TransactionCard";
import {filterTrustOptions} from "./trustPicker";
import {useRootSelector} from "../../hooks/useOutlawStore";

/**
 * Helper function for brevity.
 * @param {string} key
 * @param {object} [params]
 * @returns {string}
 */
const l = (key: string, params?: any): string => i18n.ls('transaction', key, params);

/**
 * See documentation for the Transaction schema (schemas/transaction.js) for a full rundown on how the Transaction works, and which
 * bits are used for each type of transaction.
 *
 * This card can be called from either an EditableTable or directly from a button press.  The env and startEditing props are defaulted
 * so that entering from outside an EditableTable still works.
 *
 * There are two ways that this card is shown: from an EditableTable of transactions in Financials, and directly from a Matter or Contact.
 * Check props.env.matter and props.env.contact to see if either of these cases are true.
 *
 * If we are called from a Matter, we will assume that the matter is unchangeable, but the contact (if > 1) is.
 * If we are called from a Contact, we will assume that the contact is unchangeable, but the Matter is.
 *
 * For a payment, we show a contact first, then list their cases. Click on the cases that we will apply the funds to
 */
function TransferCard(props: TransferCardProps) {
    const contacts = useRootSelector(state => state.contacts);

    // OWA-552: guarantee a non-null `current` in code, not via a prop-type contract — this
    // card never depends on the parent always passing one. props.current is present in practice.
    if (!props.current && import.meta.env.DEV) console.warn('<TransferCard/> rendered without a current Transaction; using a blank fallback.');
    const {txType, current = new Transaction({}), matter, client, onChange, element, readOnly, hasChanged, onOk, onCancel} = props;

    /**
     * Get trust accounts for the clients and matters.
     * There will be one trust per matter, and one trust for each client in common to all matters.
     *
     * However, if this is a payment, and there are multiple matters selected, there are three options:
     * 1. Put each distribution into the related client trust
     * 2. Put the whole amount into the client trust.
     * 3. Put the whole amount into
     *
     * Payments: trusts are used as a target (payment is always an external entity).
     *
     * Need to look at the 'field' parameter (to/from) to make sure we are not allowing the same to/from funding source.
     *
     * @param {string[]} types
     * @param {'to'|'from'} [field]
     */
    const getTrustAccounts = useCallback((types: string[], field: 'to' | 'from' = 'from') => {
        const tr = current;

        // Get the related matters
        const matters = CaseHelper.getMattersForClients(tr.clientIds);

        // If not clients in common, we cannot pull from any client trusts.
        const clientTrusts = types.includes('G')
                             ? tr.clientIds
                                 .filter(cid => field === 'from'
                                                ? (cid === tr.source) || FinancialHelper.getTrustAmount('G', cid, {asOf: tr.txDate ? firmCalendarDay(tr.txDate) : undefined, excludeAsOf: true}) > 0
                                                : true)
                                 .map(cid => {
                                     // Get the balance for this trust
                                     const contact = contacts.get(cid);
                                     const clientName = formatLFM(contact, true);
                                     const trustBalance = FinancialHelper.getTrustAmount('G', cid, {asOf: tr.txDate ? firmCalendarDay(tr.txDate) : undefined, excludeAsOf: true});

                                     return {
                                         id: cid,
                                         type: 'G',
                                         name: FinancialHelper.formatLabel(Icons.USER, i18n.cvs('trustFor', {value: clientName}), trustBalance, 'blue'),
                                         balance: trustBalance
                                     };
                                 })
                             : [];

        // Add the trust for the matter
        const matterTrusts = types.includes('M') ? matters
            .filter(m => field === 'from' && ['PT', 'PC', 'TT'].includes(txType)
                         ? FinancialHelper.getTrustAmount('M', m.id, {asOf: tr.txDate ? firmCalendarDay(tr.txDate) : undefined, excludeAsOf: true}) > 0
                         : true)
            .map(matter => {
                const trustAmount = FinancialHelper.getTrustAmount('M', matter.id, {asOf: tr.txDate ? firmCalendarDay(tr.txDate) : undefined, excludeAsOf: true});

                return {
                    id: matter.id,
                    type: 'M',
                    name: FinancialHelper.formatLabel(Icons.CASE, i18n.cvs('trustFor', {value: matter.name}), trustAmount, 'brown'),
                    balance: trustAmount
                };
            }) : [];

        const ordered = matter ? [...matterTrusts, ...clientTrusts] : [...clientTrusts, ...matterTrusts];

        // Source and target can not be the same, and a source needs at least as much funds as the amount (the selected source stays visible).
        const merged = filterTrustOptions(ordered, {
            excludeId: field === 'from' ? tr.target : tr.source,
            minBalance: field === 'from' ? tr.amount : undefined,
            keepIds: [tr.source]
        });

        return merged.map(account => {
            return {
                label: account.name,
                value: account.id,
                data: account
            };
        });
    }, [current, matter, contacts, txType]);

    /**
     * List trust accounts for each client and matter, along with the operating account.
     *
     * @param {Transaction} _tr
     * @param {'to'|'from'} [field]
     */
    const getTransferAccounts = useCallback((_tr: Transaction, field: 'to' | 'from' = 'from') => {
        return getTrustAccounts(['G', 'M'], field);
    }, [getTrustAccounts]);

    /**
     * Get a list of bank accounts and trust accounts.  If client(s) or a matter are selected, we can show the trust for each of them.
     * @param {Transaction} tr - the current transaction
     */
    const getSourceAccounts = useCallback((tr: Transaction) => {
        const accounts = getTransferAccounts(tr, 'from');

        // Add operating
        const operating = FinancialHelper.getOperatingAccount();
        const operatingData = {
            id: operating?.id ?? '',
            type: 'B',
            name: FinancialHelper.formatLabel(Icons.BANK, l('operating'), null, 'green'),
            balance: 0
        }
        const operatingEntry = {
            label: operatingData.name,
            value: operating?.id ?? '',
            data: operatingData
        }

        accounts.push(operatingEntry);

        if (tr) {
            return accounts.filter(at => at.data.id === tr.source);
        }
        else {
            return accounts;
        }
    }, [getTransferAccounts]);

    /**
     * Get a list of target accounts.
     *
     * If a transaction is passed, just return the object matching that transaction's target
     *
     * @param {Transaction} [tr]
     */
    const getTargetAccounts = useCallback((tr: Transaction) => {
        const accounts = getTransferAccounts(tr, 'to');

        if (tr) {
            return accounts.filter(at => at.data.id === tr.target);
        }
        else {
            return accounts;
        }
    }, [getTransferAccounts]);

    /**
     * Get a list of matters.
     *
     * For TT, only show clients that have funds in trust (matter or general).
     *
     * @param {Transaction} tr
     */
    const getClients = useCallback((tr: Transaction) => {
        // Get a unique list of all clients
        const matterClients = CaseHelper.getClients(true) as uuid[];

        const getIcon = (c: Contact) => {
            if (matterClients.includes(c.id)) return Icons.CASE;
            else return Icons.LEAD;
        };

        const getColor = (c: Contact) => {
            if (matterClients.includes(c.id)) return '#b3953c';
            else return 'green';
        };

        const clients = matterClients.filter(util.distinctFilter)
            .filter(util.existsFilter)
            .map(cid => contacts.get(cid))
            .sort(sortContacts)
            .map(c => {
                const name = formatLFM(c);
                return {
                    label: <IconButton type={getIcon(c)} color={getColor(c)} label={name}/>,
                    value: name,
                    data: c
                };
            });

        // Filter based on state, rather than the transaction
        if (tr) {
            // Only return entries for the clients in state
            return clients.filter(c => tr.clientIds.includes(c.data.id));
        }
        else return clients;
    }, [contacts]);

    /**
     *
     * @param {Array.<{label: string, value: string, data: Transaction}>} values
     */
    const onClientsChange = useCallback((values: Array<{ label: string; value: string; data: Transaction; }>) => {
        const clone = current.clone();

        // We always set the clientIds field
        clone.clientIds = values.map(v => v.data.id);

        onChange(clone);
    }, [current, onChange]);

    /**
     * Change in the matter of the funds.  Need to update matterIds here!
     *
     * @param {{label: string, value: string, data: {id: uuid, name: string}}} value
     */
    const onSourceChange = useCallback((value: { data: { id: string; type: string; }; }) => {
        const clone = current.clone();
        clone.source = value?.data.id;
        clone.sourceType = value?.data.type;

        onChange(clone);
    }, [current, onChange]);

    /**
     * Change in the target of the funds.  Need to update txType here!
     *
     * @param {{label: string, value: string, data: {id: uuid, type: string, name: string, balance: number}}} value
     */
    const onTargetChange = useCallback((value: { label: string; value: string; data: { id: uuid; type: string; name: string; balance: number; }; }) => {
        const clone = current.clone();
        clone.target = value?.data.id;
        clone.targetType = value?.data.type;

        onChange(clone);
    }, [current, onChange]);

    /**
     * @param {FormElement} element
     * @return {JSX.Element}
     */
    const renderClientName = useCallback((element: FormElement) => {
        let label = 'Client';
        let value: string;

        if (client) {
            value = formatLFM(client, true);
        }
        else if (matter) {
            label = 'Clients';

            value = matter.clients
                .map(cid => formatFMLFromId(cid, false))
                .join(', ');
        }
        else {
            const clients = current.clientIds.map(cid => formatFMLFromId(cid));
            if (clients.length > 1) label = i18n.cs('clients', true);
            value = clients.join(', ');
        }

        return element.renderElement('clientName', label, <div>{value}</div>, true);
    }, [client, current.clientIds, matter]);

    const onTransactionDateChanged = useCallback((dt: Date) => {
        const clone = current.clone();
        clone.txDate = dt;

        onChange(clone);
    }, [current, onChange]);

    /**
     * @param {FormElement} element
     */
    const renderMatterName = useCallback((element: FormElement) => {
        return element.renderElement('matterName', 'Case', <div>{matter?.name || '???'}</div>, true);
    }, [matter?.name]);

    const _readOnly = readOnly || current.invoiceId;

    // OWA-1524, OWA-1536, OWA-1535: a positive amount, and a trust source that covers it. An operating source is not checked.
    const movementBlock = FinancialHelper.getTrustMovementBlock(current);

    // We can now determine if we are clear to save.
    // TODO: This is stopping changes to an existing item, maybe because of funds?
    const disabled =
        !hasChanged ||
        !!movementBlock ||
        !current.source ||
        !current.isValid();

    const disabledMessage = failureLadder([
        [!current.txDate, l('need', {v: 'date'})],
        [current.clientIds.length === 0, l('need', {v: 'client'})],
        [!current.source, l('need', {v: 'source account'})],
        [movementBlock === 'amount', l('need', {v: 'amount', an: true})],
        [!current.target, l('need', {v: 'target account'})],
        [movementBlock === 'funds', l('insufficientFunds')],
        [!current.isValid(), current.validationError]
    ]);

    // Read-only version
    if (_readOnly) return (
        <>
            {/* Transaction Date */}
            {element.renderElement('txDate',
                <HelpMessage field='date' module='transaction'/>,
                DateUtil.formatDate(current.txDate))}

            {/* Client(s) */}
            {renderClientName(element)}

            {/* Matter(s) */}
            {matter && renderMatterName(element)}

            {/* Source */}
            {element.renderElement('source', <HelpMessage module='transaction' field='fromAccount'/>,
                getSourceAccounts(current).map((o, i) => <div key={i}>{o.label}</div>),
                true
            )}

            {/* Amount */}
            {element.renderElement('amount', 'Amount', util.formatCurrency(current.amount))}

            {/* Target */}
            {element.renderElement('target',
                <HelpMessage field='toAccount' module='transaction'/>,
                getTargetAccounts(current).map((o, i) => <div key={i}>{o.label}</div>),
                true
            )}

            {/* Notes */}
            {isPortalUser() ? element.renderElement('notes',
                                <HelpMessage module='transaction' field='notes'/>,
                                <div>{current.notes}</div>
                            )
                            : element.renderMultilineTextOnly('notes',
                    <HelpMessage module='transaction' field='notes'/>,
                    false)}

            {/* Button bar */}
            {(onOk && onCancel) &&
				<OkCancel label={i18n.ls('transaction', 'title')}
				          disabled={!!disabled}
				          disabledMessage={disabledMessage}
				          useDisabledMessage={true}
				          onCancel={onCancel}
				          onSubmit={onOk}
				          onTop={true}
                    // likesEntityType='transaction'
                    // likesEntityId={tr.id}
				/>
            }
        </>
    );
    // Not read only
    else return (
        <>
            {/* Transaction Date */}
            {element.renderDateTime('txDate',
                <HelpMessage field='date' module='transaction'/>,
                onTransactionDateChanged, false, true)}

            {/* Client(s)
                 *
                 * -- Always show! --
                 *
                 * Allow multiple clients ONLY if they both exist within the same case.  That means we will need to
                 * filter available contacts based on clients in cases.
                 *
                 * If we are passed a client or matter, that means we are within the Contacts or Cases page,
                 * and we need to be restricted to those entities.
                 */}
            {(client || matter)
             ? renderClientName(element) // Just render the client name, no choices
             : element.renderSelect('clients',
                    <HelpMessage field='clients' module='transaction'/>,
                    true, false, getClients, onClientsChange, true)
            }

            {/* Matter(s) */}
            {matter && renderMatterName(element)}

            {/* Source */}
            {element.renderSelect('source',
                <HelpMessage module='transaction' field='fromAccount'/>,
                false, false, getSourceAccounts, onSourceChange, true)}

            {/* Amount */}
            {element.renderInput('amount', 'Amount', 'money', true)}

            {/* Target */}
            {element.renderSelect('target',
                <HelpMessage field='toAccount' module='transaction'/>,
                false, false, getTargetAccounts, onTargetChange, true)
            }

            {/* Notes */}
            {element.renderMultilineTextOnly('notes',
                <HelpMessage field='notes' module='transaction'/>,
                false)}

            {/* Button bar */}
            {(onOk && onCancel) &&
				<OkCancel label={i18n.ls('transaction', 'title')}
				          disabled={!!disabled}
				          disabledMessage={disabledMessage}
				          useDisabledMessage={true}
				          onCancel={onCancel}
				          onSubmit={onOk}
				          onTop={true}
                    // likesEntityType='transaction'
                    // likesEntityId={tr.id}
				/>
            }
        </>
    );
}

export type TransferCardProps = TransactionChildCardProps & Pick<RootState, 'bankAccounts' | 'contacts' | 'matters' | 'transactions'>;

export default TransferCard;
